import React, { memo, useEffect, useImperativeHandle, useMemo, useState, } from 'react';
import { View, StyleSheet, Text, } from 'react-native';
import { useEventBusEffect } from 'hooks';
import { busEvents } from 'features/pay/constants';
import { formatAmount } from 'utils/formatting';
import useColors from 'hooks/useColors';
import { styles as textStyles, lineHeight } from './Text';
import Spacer from './Spacer';
import { rem, correct } from './values';
import ShakeContainer from './ShakeContainer';
import MoneyInputField from './MoneyInputField';
import MoneyInputPinPad from './MoneyInputPinPad';
const captions = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', '<'];
const LIMIT = 1000000;
const MANTISSA = 2;
const MoneyInput = React.forwardRef(({ onValueChange, registerShakeCallback, colors, min, restrictAmountsLessThanOne, max, showMaxHint, showMinHint, flex, onExceedMax, initialValue, header, numPadHeader, inputFieldStyle, pinPadStyle, loading, children, hideInitialMinAmountText, disableEventBus, hintOverride, alwaysDisplayMinHint = true, alwaysDisplayMaxHint = true, }, ref) => {
    const appColors = useColors();
    const shakeRef = React.useRef(undefined);
    const [value, setValue] = useState(initialValue ?? 0);
    const [fractionDigits, setFractionDigits] = useState(initialValue && initialValue % 1 !== 0 ? 3 : 0);
    const [hideMinAmount, setHideMinAmount] = useState(hideInitialMinAmountText || false);
    const [didExceedMax, setDidExceedMax] = useState(false);
    useImperativeHandle(ref, () => ({
        clear: () => {
            shakeRef.current?.();
            setValue(0);
            setDidExceedMax(false);
            setFractionDigits(0);
        },
        setAmount: (amount) => {
            if (amount > (max ?? LIMIT)) {
                if (typeof max === 'number') {
                    onExceedMax?.();
                }
                shakeRef.current?.();
            }
            else {
                setFractionDigits(Number.isInteger(amount) ? 0 : 3);
                setDidExceedMax(false);
                setValue(amount);
            }
        },
        shake: () => {
            shakeRef.current?.();
        },
    }));
    const displayValue = useMemo(() => {
        if (fractionDigits === 0)
            return formatAmount(value);
        const offset = fractionDigits > 1 ? 1 : 0;
        const formatted = formatAmount(value, undefined, Math.min(fractionDigits - offset, MANTISSA));
        if (fractionDigits === 1)
            return formatted.substring(0, formatted.length - 1);
        return formatted;
    }, [value, fractionDigits]);
    const { input, pinpad } = colors ?? {};
    useEffect(() => {
        onValueChange?.(value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    useEventBusEffect(() => {
        if (!disableEventBus) {
            setValue(0);
            setDidExceedMax(false);
        }
    }, [
        busEvents.PAYMENT_SENT,
        busEvents.REQUEST_CREATED,
        busEvents.REQUEST_FULFILLED,
    ]);
    const onInput = (text) => {
        switch (text) {
            case '<':
                switch (fractionDigits) {
                    case 0:
                        if (value === 0)
                            shakeRef.current?.();
                        else {
                            setDidExceedMax(false);
                            setValue(Math.floor(value / 10));
                        }
                        break;
                    case 1:
                        setFractionDigits(fractionDigits - 1);
                        break;
                    default: {
                        const shift = 10 ** (fractionDigits - 2);
                        const newValue = Math.floor(value * shift) / shift;
                        setFractionDigits(fractionDigits - 1);
                        setDidExceedMax(false);
                        setValue(newValue);
                    }
                }
                break;
            case '.':
                if (restrictAmountsLessThanOne && value === 0) {
                    setHideMinAmount(false);
                    shakeRef.current?.();
                }
                else if (fractionDigits === 0) {
                    setFractionDigits(1);
                }
                else {
                    shakeRef.current?.();
                }
                break;
            default: {
                let newValue = value;
                if (fractionDigits === 0) {
                    newValue = value * 10 + Number.parseInt(text, 10);
                }
                else if (fractionDigits < 3) {
                    setFractionDigits(fractionDigits + 1);
                    const shift = 10 ** fractionDigits;
                    newValue = value + Number.parseInt(text, 10) / shift;
                }
                newValue = Number(newValue.toFixed(2));
                const exceededMax = newValue > (max ?? LIMIT);
                if ((newValue === value && fractionDigits > 2) ||
                    newValue < 0 ||
                    exceededMax) {
                    shakeRef.current?.();
                    if (typeof max === 'number' && exceededMax) {
                        onExceedMax?.();
                        setDidExceedMax(true);
                    }
                }
                else {
                    setValue(newValue);
                    setDidExceedMax(false);
                }
            }
        }
    };
    const registerCallback = (callback) => {
        shakeRef.current = callback;
        registerShakeCallback?.(callback);
    };
    const hint = useMemo(() => {
        const hintColor = colors?.hintColor;
        const colorStyles = { color: hintColor ?? appColors.text.secondary };
        const canShowHintOverride = (showMaxHint ? !didExceedMax : true) &&
            (min && showMinHint ? value >= min : true);
        if (hintOverride && canShowHintOverride) {
            return <Text style={[styles.hint, colorStyles]}>{hintOverride}</Text>;
        }
        if (max !== undefined && showMaxHint) {
            if (min && value < min && showMinHint && !hideMinAmount) {
                return (<Text style={[styles.hint, colorStyles]}>
              <Text style={[styles.hintNunito, colorStyles]}>
                {formatAmount(min, 'GBP')}
              </Text>{' '}
              minimum
            </Text>);
            }
            if (alwaysDisplayMaxHint || didExceedMax) {
                return (<Text style={[styles.hint, colorStyles]}>
              <Text style={[styles.hintNunito, colorStyles]}>
                {formatAmount(max, 'GBP')}
              </Text>{' '}
              maximum
            </Text>);
            }
        }
        if (min &&
            showMinHint &&
            !hideMinAmount &&
            (alwaysDisplayMinHint || value < min)) {
            return (<Text style={[styles.hint, colorStyles]}>
            Minimum amount is{' '}
            <Text style={[styles.hintNunito, colorStyles]}>
              {formatAmount(min, 'GBP')}
            </Text>
          </Text>);
        }
        if (!alwaysDisplayMinHint || !alwaysDisplayMaxHint) {
            return <Text style={styles.hintNunito}> </Text>;
        }
        return null;
    }, [
        colors?.hintColor,
        appColors.text.secondary,
        hintOverride,
        max,
        showMaxHint,
        min,
        showMinHint,
        hideMinAmount,
        alwaysDisplayMinHint,
        value,
        alwaysDisplayMaxHint,
        didExceedMax,
    ]);
    return (<View style={[styles.container, flex && styles.flex]}>
        {header || (flex && <View style={styles.flex}/>)}
        <ShakeContainer shakeOnMount={false} registerCallback={registerCallback}>
          <MoneyInputField style={[inputFieldStyle]} color={input}>
            {displayValue}
          </MoneyInputField>
        </ShakeContainer>
        {hint}
        {numPadHeader ?? <Spacer flex/>}
        {children}
        <View pointerEvents={loading ? 'none' : 'auto'}>
          <MoneyInputPinPad style={[styles.pinpad, pinPadStyle]} captions={captions} onInput={onInput} color={pinpad}/>
        </View>
      </View>);
});
const styles = StyleSheet.create({
    container: {
        flexDirection: 'column',
        alignItems: 'center',
    },
    flex: {
        flex: 1,
    },
    pinpad: {
        marginTop: correct(25.5),
    },
    hint: {
        alignSelf: 'center',
        textAlign: 'center',
        ...textStyles.TextThin_14,
    },
    hintNunito: {
        fontFamily: 'Nunito-Regular',
        fontSize: rem(14),
        letterSpacing: 0.5,
        lineHeight: rem(lineHeight.TextThin_14),
    },
});
export default memo(MoneyInput);
